import { useEffect, useState } from 'react'
import { IGuarantorOption } from 'components/modals/guarantorPicker'
import { getOneMonthLater } from 'utilities/helper/getOneMonthLater'
import { ITerms } from './steps/StepTerms'

/**
 * A function rather than a frozen constant: `startDate` is relative to today,
 * and a PWA left open overnight would otherwise offer yesterday's answer.
 */
export const emptyTerms = (): ITerms => ({
    installments: '',
    feeType: 'percentage',
    fee: '',
    feeMode: 'lip_ma',
    // The first instalment falls a month out unless the manager says
    // otherwise — the same default Android has always opened on, and the
    // one `useGrantSubmit` already falls back to when this is blank.
    startDate: getOneMonthLater(),
    pattern: 'equal',
    custom: null,
})

interface IStoredDraft {
    borrower?: ILoanCandidate
    amount: string
    terms: ITerms
    guarantors: IGuarantorOption[]
    note: string
    /**
     * The Bale nudge this grant started from.
     *
     * Kept in the DRAFT rather than read off the URL at submit time, because
     * `setSearchParams({ step })` replaces the whole query string — so the
     * `?nudge=` the button arrived with is gone by step 2, and `loan_granted`
     * was landing with `nudge_id: null` on every single grant. Measured: 9
     * real button opens carried a nudge_id, 0 completed grants did, so the
     * funnel's last step could only be joined by person-and-timestamp by
     * hand. In the draft it survives both the URL rewrite and the
     * sessionStorage round-trip.
     */
    nudgeId?: string | null
}

const key = (moneypoolId?: string) => `hamyan:grant-draft:${moneypoolId ?? ''}`

/**
 * The wizard's answers, kept across an unmount.
 *
 * The payout step can send the manager off to `/manage-bank-cards/add` — the
 * borrower may have no registered account. That unmounts the wizard, and on
 * the way back the "no borrower picked" guard bounced them to step 1 with all
 * four answered questions gone. Reported from real use: «when clicking back
 * from the create-bank-account page, we return to step 1 :(».
 *
 * **sessionStorage, and restored in a `useState` initialiser, not an effect.**
 * The initialiser matters: the step lives in the URL, so on the first render
 * back the wizard is already at `?step=5`, and a draft that only lands on the
 * second render lets the guard fire first and redirect anyway.
 *
 * sessionStorage rather than localStorage because a half-built loan is
 * session-scoped by nature — closing the tab should not leave a stale draft
 * waiting to be mistaken for a real one tomorrow.
 */
export default function useGrantDraft(moneypoolId?: string) {
    const stored = (): Partial<IStoredDraft> => {
        try {
            const raw = sessionStorage.getItem(key(moneypoolId))
            return raw ? JSON.parse(raw) : {}
        } catch {
            // a corrupt or unavailable store must not take the page down;
            // the worst case is an empty wizard, which is where it started
            return {}
        }
    }

    const initial = stored()
    const [borrower, setBorrower] = useState<ILoanCandidate | undefined>(
        initial.borrower
    )
    const [amount, setAmount] = useState(initial.amount ?? '')
    const [terms, setTerms] = useState<ITerms>(initial.terms ?? emptyTerms())
    const [guarantors, setGuarantors] = useState<IGuarantorOption[]>(
        initial.guarantors ?? []
    )
    const [note, setNote] = useState(initial.note ?? '')
    // Sticky: once a nudge brought the manager here, that stays true for the
    // rest of the wizard no matter how the URL is rewritten underneath it.
    const [nudgeId, setNudgeIdState] = useState<string | null>(
        initial.nudgeId ?? null
    )
    const setNudgeId = (next?: string | null) => {
        // never unset — a later render with a stripped URL must not erase it
        if (next) setNudgeIdState(next)
    }

    useEffect(() => {
        try {
            sessionStorage.setItem(
                key(moneypoolId),
                JSON.stringify({
                    borrower,
                    amount,
                    terms,
                    guarantors,
                    note,
                    nudgeId,
                })
            )
        } catch {
            // storage full or blocked — the wizard still works in-memory
        }
    }, [moneypoolId, borrower, amount, terms, guarantors, note, nudgeId])

    const clear = () => {
        try {
            sessionStorage.removeItem(key(moneypoolId))
        } catch {
            /* nothing to clean up */
        }
    }

    return {
        nudgeId,
        setNudgeId,
        borrower,
        setBorrower,
        amount,
        setAmount,
        terms,
        setTerms,
        guarantors,
        setGuarantors,
        note,
        setNote,
        clear,
    }
}
